import { mdiCheck, mdiClose, mdiHistory, mdiMagnify, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { ConditionKind } from '../../../services/worker-configuration/automation/worker-automation.types';
import { formatPayerAdjustmentGroupShortCode } from '../../../services/dictionary/payer-adjustment-groups';
import { formatCatalogGroupLabel } from '../../../services/worker-configuration';
import { getUsageBadgePresentation } from '../../../services/worker-configuration/automation/worker-automation.utils';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import {
    formatCatalogPickerResultCount,
    formatCatalogReportCountLabel,
    isCatalogFrequencySortedKind,
    splitCatalogOptionMessage,
} from '../configuration.utils';
import * as S from './configuration-rule.styles';

interface IConfigurationRuleConditionEditorProps {
    disabled?: boolean;
    hint: string;
    kind: ConditionKind;
    label: string;
    matchIndex: number;
    placeholder: string;
    ruleId: string;
    store: WorkerStore;
    values: string[];
}

interface ICatalogPoolItem {
    count?: number;
    label: string;
    sub: string;
    title: string;
}

interface ICatalogPickerOption extends ICatalogPoolItem {
    badge: ReturnType<typeof getUsageBadgePresentation>;
    custom: false;
}

interface ICustomPickerOption {
    badge: ReturnType<typeof getUsageBadgePresentation>;
    custom: true;
    label: string;
    sub: string;
    title: string;
}

function mapCodeCatalogItem(item: { code: string; count?: number; desc?: string }): ICatalogPoolItem {
    const message = item.desc?.trim() ?? '';
    const split = splitCatalogOptionMessage(message);

    if (split.description) {
        return {
            label: item.code,
            title: split.title,
            sub: split.description,
            count: item.count,
        };
    }

    return {
        label: item.code,
        title: item.code,
        sub: message,
        count: item.count,
    };
}

function mapTransmissionCatalogItem(item: { code: string; count?: number }): ICatalogPoolItem {
    const split = splitCatalogOptionMessage(item.code);

    return {
        label: item.code,
        title: split.title,
        sub: split.description,
        count: item.count,
    };
}

function buildCatalogPool(kind: ConditionKind, catalog: NonNullable<WorkerStore['catalog']>): ICatalogPoolItem[] {
    if (kind === 'carcGroup') {
        return catalog.carcGroups.map(item => ({
            label: item.group,
            title: formatPayerAdjustmentGroupShortCode(item.group),
            sub: formatCatalogGroupLabel(item.group),
            count: item.count,
        }));
    }
    if (kind === 'carcCode') {
        return catalog.carcCodes.map(mapCodeCatalogItem);
    }
    if (kind === 'rarcCode') {
        return catalog.rarcCodes.map(mapCodeCatalogItem);
    }
    if (kind === 'procedureCode') {
        return [];
    }

    return catalog.rejectionStatuses.map(mapTransmissionCatalogItem);
}

function catalogSearchPlaceholder(kind: ConditionKind): string {
    switch (kind) {
        case 'carcGroup':
            return 'Search CARC groups…';
        case 'carcCode':
            return 'Search CARC codes…';
        case 'rarcCode':
            return 'Search RARC codes…';
        case 'procedureCode':
            return 'Type a procedure code…';
        default:
            return 'Search transmission statuses…';
    }
}

export const ConfigurationRuleConditionEditor: React.FC<IConfigurationRuleConditionEditorProps> = observer(
    ({ disabled, hint, kind, label, matchIndex, placeholder, ruleId, store, values }) => {
        const pickerKey = `${ruleId}|${matchIndex}|${kind}`;
        const isOpen = store.openPickerKey === pickerKey;
        const catalog = store.catalog;
        const allowsCustomValue = kind === 'contains' || kind === 'procedureCode';
        const catalogReady = Boolean(catalog) || kind === 'procedureCode';
        const showFrequencySortBadge = isCatalogFrequencySortedKind(kind);

        const { catalogOptions, customOption, totalMatches } = useMemo(() => {
            if (!isOpen) {
                return {
                    catalogOptions: [] as ICatalogPickerOption[],
                    customOption: null as ICustomPickerOption | null,
                    totalMatches: 0,
                };
            }

            if (!catalog && kind !== 'procedureCode') {
                return {
                    catalogOptions: [] as ICatalogPickerOption[],
                    customOption: null as ICustomPickerOption | null,
                    totalMatches: 0,
                };
            }

            const query = store.pickerQuery.trim().toLowerCase();
            const existing = new Set(values.map(String));
            const pool = catalog ? buildCatalogPool(kind, catalog) : [];

            const matches = pool.filter(
                item =>
                    !existing.has(String(item.label)) &&
                    (query === '' ||
                        `${item.label} ${item.title} ${item.sub}`.toLowerCase().includes(query)),
            );

            const catalogOptions: ICatalogPickerOption[] = matches.map(item => ({
                ...item,
                custom: false,
                badge: getUsageBadgePresentation(store.getCatalogUsage(ruleId, matchIndex, kind, String(item.label))),
            }));

            const exact = pool.some(item => String(item.label).toLowerCase() === query);
            const customLabel = store.pickerQuery.trim();
            const customOption: ICustomPickerOption | null =
                allowsCustomValue && customLabel !== '' && !exact && !existing.has(customLabel)
                    ? {
                          label: customLabel,
                          title: customLabel,
                          sub:
                              kind === 'procedureCode'
                                  ? 'Add procedure code'
                                  : 'User typed — add as a custom value',
                          custom: true,
                          badge: getUsageBadgePresentation(
                              store.getCatalogUsage(ruleId, matchIndex, kind, customLabel),
                          ),
                      }
                    : null;

            return {
                catalogOptions,
                customOption,
                totalMatches: matches.length,
            };
        }, [allowsCustomValue, catalog, isOpen, kind, matchIndex, ruleId, store.pickerQuery, values]);

        const options = customOption ? [...catalogOptions, customOption] : catalogOptions;
        const catalogPoolSize = catalog ? buildCatalogPool(kind, catalog).length : 0;

        const handlePick = (value: string): void => {
            store.addConditionChip(ruleId, matchIndex, kind, value);
        };

        const showPicker = isOpen && catalogReady && !disabled;
        const showEmptyState = showPicker && options.length === 0 && store.pickerQuery.trim() !== '';
        const showFreeTextHint =
            showPicker && allowsCustomValue && catalogPoolSize === 0 && !store.pickerQuery.trim();
        const freeTextHint =
            kind === 'procedureCode'
                ? 'Type a procedure code and press Enter to add it.'
                : 'Type a rejection message and press Enter to add it.';

        return (
            <S.ConditionCard>
                <S.ConditionHeader>
                    <S.ConditionTitle>{label}</S.ConditionTitle>
                    <S.ConditionHint>{hint}</S.ConditionHint>
                </S.ConditionHeader>

                <S.ChipRow>
                    {values.map((value, index) => {
                        const chipBadge = getUsageBadgePresentation(
                            store.getCatalogUsage(ruleId, matchIndex, kind, String(value)),
                        );

                        return (
                            <S.Chip key={`${value}-${index}`} title={chipBadge.title || undefined}>
                                {kind === 'carcGroup'
                                    ? formatPayerAdjustmentGroupShortCode(String(value))
                                    : value}
                                {chipBadge.used && (
                                    <S.ChipUsageBadge variant="used">
                                        <Icon path={mdiHistory} size={0.55} aria-hidden={true} />
                                        {chipBadge.short}
                                    </S.ChipUsageBadge>
                                )}
                                <S.ChipRemove
                                    type="button"
                                    disabled={disabled}
                                    aria-label={`Remove ${value}`}
                                    onClick={() => store.removeConditionChip(ruleId, matchIndex, kind, index)}>
                                    <Icon path={mdiClose} size={0.55} aria-hidden={true} />
                                </S.ChipRemove>
                            </S.Chip>
                        );
                    })}

                    <S.ChipInputWrap>
                        {catalogReady && <Icon path={mdiMagnify} size={0.6} color="#8e9293" aria-hidden={true} />}
                        <S.ChipInput
                            placeholder={catalogReady ? catalogSearchPlaceholder(kind) : placeholder}
                            disabled={disabled}
                            readOnly={disabled}
                            value={isOpen ? store.pickerQuery : ''}
                            onFocus={() => {
                                if (!disabled) {
                                    store.setPickerState(pickerKey, '');
                                }
                            }}
                            onChange={event => {
                                if (!disabled) {
                                    store.setPickerState(pickerKey, event.target.value);
                                }
                            }}
                            onBlur={() => {
                                window.setTimeout(() => {
                                    if (store.openPickerKey === pickerKey) {
                                        store.setPickerState(null);
                                    }
                                }, 170);
                            }}
                            onKeyDown={event => {
                                if (disabled) {
                                    return;
                                }

                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    const value = event.currentTarget.value.trim();
                                    if (value) {
                                        handlePick(value);
                                    }
                                }
                            }}
                        />
                    </S.ChipInputWrap>
                </S.ChipRow>

                {showPicker && (options.length > 0 || showEmptyState || showFreeTextHint) && (
                    <S.ConditionPickerPanel>
                        <S.ConditionPickerDropdown>
                            {showFreeTextHint ? (
                                <S.ConditionPickerMeta>
                                    {freeTextHint}
                                </S.ConditionPickerMeta>
                            ) : (
                                <>
                                    {totalMatches > 0 && (
                                        <S.ConditionPickerMeta>
                                            <span>
                                                {formatCatalogPickerResultCount(
                                                    totalMatches,
                                                    store.pickerQuery,
                                                )}
                                            </span>
                                            {showFrequencySortBadge ? (
                                                <S.ConditionPickerSortBadge>
                                                    Sorted by frequency
                                                </S.ConditionPickerSortBadge>
                                            ) : null}
                                        </S.ConditionPickerMeta>
                                    )}
                                    {options.length > 0 ? (
                                        <>
                                            {catalogOptions.map(option => (
                                                <S.PickerOption
                                                    key={`${option.label}-catalog`}
                                                    type="button"
                                                    onMouseDown={event => {
                                                        event.preventDefault();
                                                        handlePick(option.label);
                                                    }}>
                                                    <S.PickerOptionContent>
                                                        <S.PickerTitle>{option.title}</S.PickerTitle>
                                                        {option.sub ? (
                                                            <S.PickerDesc>{option.sub}</S.PickerDesc>
                                                        ) : null}
                                                    </S.PickerOptionContent>
                                                    {option.count != null && option.count > 0 && (
                                                        <S.ReportCountBadge>
                                                            {formatCatalogReportCountLabel(option.count)}
                                                        </S.ReportCountBadge>
                                                    )}
                                                    {option.badge.used ? (
                                                        <S.UsageBadge variant="used">
                                                            <Icon path={mdiHistory} size={0.65} aria-hidden={true} />
                                                            {option.badge.label}
                                                        </S.UsageBadge>
                                                    ) : (
                                                        <S.UsageBadge variant="fresh">
                                                            <Icon path={mdiCheck} size={0.65} aria-hidden={true} />
                                                            {option.badge.label}
                                                        </S.UsageBadge>
                                                    )}
                                                </S.PickerOption>
                                            ))}
                                            {customOption && (
                                                <S.PickerOption
                                                    key={`${customOption.label}-custom`}
                                                    type="button"
                                                    custom
                                                    onMouseDown={event => {
                                                        event.preventDefault();
                                                        handlePick(customOption.label);
                                                    }}>
                                                    <Icon path={mdiPlus} size={0.7} color="#2E6BE6" aria-hidden={true} />
                                                    <S.PickerOptionContent>
                                                        <S.PickerTitle>Add “{customOption.label}”</S.PickerTitle>
                                                        <S.PickerDesc>user typed</S.PickerDesc>
                                                    </S.PickerOptionContent>
                                                </S.PickerOption>
                                            )}
                                        </>
                                    ) : (
                                        <S.PickerOption type="button" disabled>
                                            <S.PickerCode>No matches found</S.PickerCode>
                                        </S.PickerOption>
                                    )}
                                </>
                            )}
                        </S.ConditionPickerDropdown>
                    </S.ConditionPickerPanel>
                )}
            </S.ConditionCard>
        );
    },
);
